import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, ButtonBase, Collapse, IconButton, Tooltip, Typography } from "@mui/material";
import {
  Check as CheckIcon,
  ExpandMore as ChevronIcon,
  Link as LinkIcon,
  UnfoldLess as PlegarIcon,
  UnfoldMore as DesplegarIcon,
  ListAlt as IndiceIcon,
} from "@mui/icons-material";
import RichContent, { estilosContenido } from "./RichContent";
import { analizarContenido, Seccion } from "../../utils/contenidoHtml";
import { tokens } from "../../theme";

/**
 * Un bloque de contenido largo, con índice y secciones plegables.
 *
 * Las preguntas frecuentes son treinta pantallas de texto: leerlas de corrido
 * está bien, pero volver a una en particular no. Así que el mismo HTML se pinta
 * partido por sus títulos —cada uno se pliega— y con un índice pegado a la
 * izquierda que sigue a la lectura y lleva a cualquier punto de un clic.
 *
 * Todo se apoya en las anclas que pone `utils/contenidoHtml`: quien escribe solo
 * pone títulos. Un enlace `#ancla` dentro del texto abre la sección que lo
 * contiene antes de saltar, y cada título ofrece su dirección para compartirla.
 *
 * Si el contenido no tiene al menos dos títulos no hay nada que navegar y cae en
 * el render normal (`RichContent`).
 */

/** Ancho del índice y de la columna de lectura (los usa la página para alinear). */
export const ANCHO_INDICE = 244;
export const ANCHO_LECTURA = 820;

/** Para que lo que va bajo el bloque (los coordinadores) siga la columna de lectura. */
export const sxColumnaContenido = { maxWidth: ANCHO_LECTURA, ml: { md: `${ANCHO_INDICE + 32}px` } };

/** Cuánto dura la confirmación de "copiado" en el botón del ancla. */
const AVISO_MS = 2000;

/** Lo que dura la animación del plegado. */
const ANIMACION_MS = 200;

/**
 * Deja el título arriba de la pantalla.
 *
 * Salto seco, como el de un ancla de toda la vida: el desplazamiento suave
 * depende de que el navegador anime, y hay entornos donde no se mueve. Se repite
 * en el cuadro siguiente porque el plegado que se acaba de abrir puede estar
 * todavía ajustando su alto cuando se midió la primera vez.
 */
function saltarA(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ block: "start" });
  requestAnimationFrame(() => el.scrollIntoView({ block: "start" }));
}

export default function ContenidoNavegable({ html, accion }: { html: string; accion?: ReactNode }) {
  const { secciones, titulos } = useMemo(() => analizarContenido(html), [html]);
  const [cerradas, setCerradas] = useState<ReadonlySet<string>>(new Set());
  const [activo, setActivo] = useState("");
  const [indiceMovil, setIndiceMovil] = useState(false);
  /** Ancla a la que se está yendo, mientras se abre lo que la tapaba. */
  const pendiente = useRef<string | null>(null);

  /** Ancla → sección que la contiene, para abrirla antes de saltar a un subtítulo. */
  const seccionDe = useMemo(() => {
    const mapa = new Map<string, string>();
    secciones.forEach(({ id, subs }) => id && subs.forEach((x) => mapa.set(x.id, id)));
    return mapa;
  }, [secciones]);

  const irA = useCallback(
    (id: string) => {
      let abrio = false;
      setCerradas((previas) => {
        const padre = seccionDe.get(id);
        if (!previas.has(id) && !(padre && previas.has(padre))) return previas;
        abrio = true;
        const s = new Set(previas);
        s.delete(id);
        if (padre) s.delete(padre);
        return s;
      });
      setActivo(id);
      setIndiceMovil(false);
      // La dirección queda con el ancla para poder compartirla o recargar ahí.
      window.history.replaceState(null, "", `#${id}`);
      // Salto seco, como el de un ancla de toda la vida: el suave depende de que
      // el navegador anime, y hay entornos donde simplemente no se mueve.
      // Si hubo que abrir algo, este primer salto cae corto —lo de arriba todavía
      // está creciendo— y lo repite `alAbrir` cuando el plegado termina.
      if (abrio) pendiente.current = id;
      window.setTimeout(() => saltarA(id), 0);
    },
    [seccionDe],
  );

  /** Terminó de abrirse un plegado: si se iba a un ancla, ahora sí está en su sitio. */
  const alAbrir = () => {
    if (pendiente.current) saltarA(pendiente.current);
  };

  // Entrar directo a un ancla (un enlace compartido, o recargar la página).
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (id && titulos.some((t) => t.id === id)) irA(id);
    // Solo al montar o si cambia el contenido: después manda la navegación del usuario.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titulos]);

  // Qué título se está leyendo, para destacarlo en el índice.
  useEffect(() => {
    const nodos = titulos.map((t) => document.getElementById(t.id)).filter((n): n is HTMLElement => !!n);
    if (!nodos.length) return;
    const visibles = new Set<string>();
    const observador = new IntersectionObserver(
      (entradas) => {
        entradas.forEach((e) => (e.isIntersecting ? visibles.add(e.target.id) : visibles.delete(e.target.id)));
        const primero = titulos.find((t) => visibles.has(t.id));
        if (primero) setActivo(primero.id);
      },
      // Banda de lectura: la franja superior de la pantalla, bajo la barra.
      { rootMargin: "-88px 0px -68% 0px" },
    );
    nodos.forEach((n) => observador.observe(n));
    return () => observador.disconnect();
  }, [titulos, cerradas]);

  // Abrir a mano cancela el salto pendiente: si no, terminar de abrir arrastraría
  // la página de vuelta al ancla anterior.
  const alternar = (id: string) => {
    pendiente.current = null;
    setCerradas((previas) => {
      const s = new Set(previas);
      if (!s.delete(id)) s.add(id);
      return s;
    });
  };

  const plegables = useMemo(() => titulos.map((t) => t.id), [titulos]);
  const todoPlegado = cerradas.size >= plegables.length && plegables.length > 0;
  const alternarTodo = () => {
    pendiente.current = null;
    setCerradas(todoPlegado ? new Set() : new Set(plegables));
  };

  // Un enlace interno del texto (`href="#ancla"`) tiene que abrir su sección
  // antes de saltar; si no, el navegador salta a algo que está plegado.
  const alPulsar = (e: React.MouseEvent) => {
    const destino = (e.target as HTMLElement).closest("a")?.getAttribute("href");
    if (!destino?.startsWith("#")) return;
    e.preventDefault();
    irA(decodeURIComponent(destino.slice(1)));
  };

  if (titulos.length < 2) {
    return (
      <>
        {accion}
        <RichContent html={html} />
      </>
    );
  }

  const indice = <ListaIndice secciones={secciones} activo={activo} onIr={irA} />;
  const botonPlegar = (
    <ButtonBase
      onClick={alternarTodo}
      sx={{
        gap: 0.5,
        px: 0.75,
        py: 0.5,
        borderRadius: `${tokens.boton.radio}px`,
        color: "text.secondary",
        fontSize: 12.5,
        fontWeight: 600,
        "&:hover": { bgcolor: tokens.marca.tenue },
      }}
    >
      {todoPlegado ? <DesplegarIcon sx={{ fontSize: 16 }} /> : <PlegarIcon sx={{ fontSize: 16 }} />}
      {todoPlegado ? "Desplegar todo" : "Plegar todo"}
    </ButtonBase>
  );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { md: `${ANCHO_INDICE}px minmax(0, ${ANCHO_LECTURA}px)` },
        gap: { md: 4 },
        alignItems: "start",
      }}
    >
      {/* Índice pegajoso: acompaña el scroll y tiene el suyo si no cabe. */}
      <Box
        component="nav"
        aria-label="Contenido de la página"
        sx={{
          display: { xs: "none", md: "block" },
          position: "sticky",
          top: 24,
          maxHeight: "calc(100vh - 48px)",
          overflowY: "auto",
          pr: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{ display: "block", fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", color: "text.secondary", mb: 0.5 }}
        >
          En esta página
        </Typography>
        <Box sx={{ mb: 1 }}>{botonPlegar}</Box>
        {indice}
      </Box>

      <Box sx={{ minWidth: 0 }} onClick={alPulsar}>
        {accion}

        {/* En móvil el índice no cabe al lado: va arriba, plegado. */}
        <Box sx={{ display: { md: "none" }, mb: 2, border: "1px solid", borderColor: "divider", borderRadius: "8px" }}>
          <ButtonBase
            onClick={() => setIndiceMovil((v) => !v)}
            aria-expanded={indiceMovil}
            sx={{ width: "100%", justifyContent: "flex-start", gap: 1, p: 1.25, fontSize: 14, fontWeight: 600, color: tokens.marca.texto }}
          >
            <IndiceIcon sx={{ fontSize: 18 }} />
            Contenido de la página
            <ChevronIcon sx={{ fontSize: 20, ml: "auto", transform: indiceMovil ? "none" : "rotate(-90deg)", transition: "transform .2s" }} />
          </ButtonBase>
          <Collapse in={indiceMovil}>
            <Box sx={{ px: 1.25, pb: 1.25 }}>
              <Box sx={{ mb: 1 }}>{botonPlegar}</Box>
              {indice}
            </Box>
          </Collapse>
        </Box>

        {secciones.map((s, i) => (
          <SeccionPlegable
            key={s.id ?? "preambulo"}
            seccion={s}
            primera={i === 0}
            abierta={!s.id || !cerradas.has(s.id)}
            cerradas={cerradas}
            onAlternar={alternar}
            onAbierta={alAbrir}
          />
        ))}
      </Box>
    </Box>
  );
}

/**
 * Una sección (`h2`) con sus subsecciones (`h3`), cada una plegable por su cuenta.
 *
 * El tramo anterior al primer `h2` no tiene título: va suelto, sin plegar, pero
 * con sus subsecciones —un bloque puede empezar directo con subtítulos—.
 */
function SeccionPlegable({
  seccion,
  primera,
  abierta,
  cerradas,
  onAlternar,
  onAbierta,
}: {
  seccion: Seccion;
  primera: boolean;
  abierta: boolean;
  cerradas: ReadonlySet<string>;
  onAlternar: (id: string) => void;
  onAbierta: () => void;
}) {
  const cuerpo = (
    <>
      {seccion.intro.trim() && <Box sx={estilosContenido} dangerouslySetInnerHTML={{ __html: seccion.intro }} />}
      {seccion.subs.map((sub) => (
        <Box key={sub.id}>
          <TituloPlegable nivel={3} id={sub.id} texto={sub.texto} abierta={!cerradas.has(sub.id)} onAlternar={onAlternar} />
          <Collapse in={!cerradas.has(sub.id)} timeout={ANIMACION_MS} onEntered={onAbierta} id={`cuerpo-${sub.id}`}>
            <Box sx={estilosContenido} dangerouslySetInnerHTML={{ __html: sub.html }} />
          </Collapse>
        </Box>
      ))}
    </>
  );

  if (!seccion.id) return <Box component="section">{cuerpo}</Box>;

  return (
    <Box component="section">
      <TituloPlegable
        nivel={2}
        id={seccion.id}
        texto={seccion.texto ?? ""}
        abierta={abierta}
        primera={primera}
        onAlternar={onAlternar}
      />
      <Collapse in={abierta} timeout={ANIMACION_MS} onEntered={onAbierta} id={`cuerpo-${seccion.id}`}>
        {cuerpo}
      </Collapse>
    </Box>
  );
}

/**
 * Título que abre y cierra lo que tiene debajo. El `id` va en el propio `h2`/`h3`
 * —es el ancla— y el botón queda dentro, que es el patrón accesible: el título
 * sigue siendo título y el lector de pantalla anuncia si está abierto.
 */
function TituloPlegable({
  nivel,
  id,
  texto,
  abierta,
  primera = false,
  onAlternar,
}: {
  nivel: 2 | 3;
  id: string;
  texto: string;
  abierta: boolean;
  primera?: boolean;
  onAlternar: (id: string) => void;
}) {
  const esSeccion = nivel === 2;
  return (
    <Box
      component={esSeccion ? "h2" : "h3"}
      id={id}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        m: 0,
        scrollMarginTop: "88px",
        fontFamily: tokens.fuente.titulos,
        ...(esSeccion
          ? {
              fontWeight: 700,
              fontSize: { xs: 22, md: 28 },
              color: tokens.marca.texto,
              mt: primera ? 0 : 6,
              mb: 2,
              pb: 1,
              borderBottom: "2px solid",
              borderColor: "divider",
            }
          : { fontWeight: 600, fontSize: { xs: 16.5, md: 18 }, mt: 3, mb: 1 }),
        "&:hover .ancla, &:focus-within .ancla": { opacity: 1 },
      }}
    >
      <ButtonBase
        onClick={() => onAlternar(id)}
        aria-expanded={abierta}
        aria-controls={`cuerpo-${id}`}
        sx={{
          flex: 1,
          minWidth: 0,
          justifyContent: "flex-start",
          textAlign: "left",
          gap: 0.75,
          px: 0.5,
          py: 0.25,
          ml: -0.5,
          borderRadius: `${tokens.boton.radio}px`,
          font: "inherit",
          color: "inherit",
          "&:hover": { bgcolor: tokens.marca.tenue },
        }}
      >
        <ChevronIcon
          sx={{
            fontSize: esSeccion ? 26 : 20,
            flexShrink: 0,
            color: "text.secondary",
            transform: abierta ? "none" : "rotate(-90deg)",
            transition: "transform .2s",
          }}
        />
        <Box component="span" sx={{ minWidth: 0 }}>
          {texto}
        </Box>
      </ButtonBase>
      <CopiarAncla id={id} texto={texto} />
    </Box>
  );
}

/** Copia la dirección de esta sección: es lo que se pega en un correo o en el foro. */
function CopiarAncla({ id, texto }: { id: string; texto: string }) {
  const [copiado, setCopiado] = useState(false);

  const copiar = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), AVISO_MS);
    } catch {
      // Sin portapapeles (o sin HTTPS) al menos deja la dirección en la barra.
      window.location.hash = id;
    }
  };

  return (
    <Tooltip title={copiado ? "Dirección copiada" : "Copiar la dirección de esta sección"}>
      <IconButton
        className="ancla"
        size="small"
        onClick={copiar}
        aria-label={`Copiar la dirección de “${texto}”`}
        sx={{ flexShrink: 0, opacity: { xs: 0.5, md: 0 }, transition: "opacity .15s", color: "text.secondary" }}
      >
        {copiado ? <CheckIcon sx={{ fontSize: 16 }} /> : <LinkIcon sx={{ fontSize: 16 }} />}
      </IconButton>
    </Tooltip>
  );
}

/** El índice: secciones y, debajo, sus subtítulos. */
function ListaIndice({ secciones, activo, onIr }: { secciones: Seccion[]; activo: string; onIr: (id: string) => void }) {
  return (
    <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
      {secciones.map((s) => (
        <Box component="li" key={s.id ?? "preambulo"} sx={{ mb: 0.5 }}>
          {s.id && <ItemIndice texto={s.texto ?? ""} nivel={2} activo={activo === s.id} onPulsar={() => onIr(s.id as string)} />}
          {s.subs.length > 0 && (
            <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
              {s.subs.map((sub) => (
                <Box component="li" key={sub.id}>
                  <ItemIndice texto={sub.texto} nivel={3} activo={activo === sub.id} onPulsar={() => onIr(sub.id)} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

function ItemIndice({ texto, nivel, activo, onPulsar }: { texto: string; nivel: 2 | 3; activo: boolean; onPulsar: () => void }) {
  const esSeccion = nivel === 2;
  return (
    <ButtonBase
      onClick={onPulsar}
      aria-current={activo ? "location" : undefined}
      sx={{
        display: "block",
        width: "100%",
        textAlign: "left",
        py: 0.5,
        pl: esSeccion ? 1 : 2,
        pr: 1,
        borderLeft: "2px solid",
        borderColor: activo ? tokens.naranjo[400] : "divider",
        bgcolor: activo ? tokens.marca.tenue : "transparent",
        fontSize: esSeccion ? 13.5 : 13,
        fontWeight: esSeccion ? 600 : 400,
        lineHeight: 1.35,
        color: activo ? tokens.marca.texto : esSeccion ? "text.primary" : "text.secondary",
        "&:hover": { bgcolor: tokens.marca.tenue, color: tokens.marca.texto },
      }}
    >
      {/* Las preguntas son largas: dos líneas y el resto se corta. El recorte va
          en el texto, no en el botón, o se comería el aire entre un ítem y otro. */}
      <Box
        component="span"
        sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", maxHeight: "2.7em" }}
      >
        {texto}
      </Box>
    </ButtonBase>
  );
}
